import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { BsMicMuteFill } from 'react-icons/bs';
import { useShallow } from 'zustand/react/shallow';

import { AvatarCircle } from '@src/components';
import useConsumerControl from '@src/components/VideoBox/hooks/useConsumerControl';
import { getMediaStreamSource, isValidVideoStream } from '@src/helpers';
import { usePeersStore } from '@src/stores';
import { debounce } from '@src/utils';

type TPipMediaType = 'videoType' | 'screenType';

const PIP_COMPACT_AVATAR_SIZE_PX = 72;

const PIP_COMPACT_SPEAKING_DEBOUNCE_MS = 2000;
const PIP_COMPACT_SPEAKING_THRESHOLD = 3;

const PIP_COMPACT_VIDEO_READY_DELAY_MS = 500;

const PIP_COMPACT_MIC_BADGE_SIZE_PX = 20;
const PIP_COMPACT_MIC_BADGE_BORDER_PX = 3;
const PIP_COMPACT_MIC_ICON_SIZE_PX = 12;

const PIP_COMPACT_ACTIVE_RING_PADDING_PX = 3;
const PIP_COMPACT_ACTIVE_RING_BORDER_PX = 3;
const PIP_COMPACT_ACTIVE_RING_SHADOW_PX = 20;

interface IPipCompactAvatarProps {
  id: string;
  type: TPipMediaType;
  stream: MediaStream | null;
  poster?: string;
  peerId: string;
  muted: boolean;
  audioVolume?: number;
  scale?: number;
}

const PipCompactAvatar = memo(function PipCompactAvatar({
  id,
  type,
  stream,
  poster,
  peerId,
  muted,
  audioVolume,
  scale = 1,
}: IPipCompactAvatarProps) {
  // store selectors
  const { peerInfo } = usePeersStore(
    useShallow((state) => ({
      peerInfo: state.getPeerInfoById(peerId),
    }))
  );

  // derived state
  const isMicMuted = peerInfo ? !peerInfo.peer_audio : (audioVolume ?? 0) === 0 && muted;
  const hasValidCameraStream = type === 'videoType' && !!stream && isValidVideoStream(stream);

  // refs + local state
  const videoRef = useRef<HTMLVideoElement>(null);
  const readyTimeoutRef = useRef<number | null>(null);

  const [isPeerTalking, setIsPeerTalking] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  // memos
  const debouncedSetPeerNotTalking = useMemo(
    () => debounce(() => setIsPeerTalking(false), PIP_COMPACT_SPEAKING_DEBOUNCE_MS),
    []
  );

  const { handleVisibilityChange } = useConsumerControl(id);
  useEffect(() => {
    if (!isMicMuted && (audioVolume ?? 0) > PIP_COMPACT_SPEAKING_THRESHOLD) {
      setIsPeerTalking(true);
      debouncedSetPeerNotTalking.cancel?.();
    } else if (isMicMuted) {
      setIsPeerTalking(false);
      debouncedSetPeerNotTalking.cancel?.();
    } else {
      debouncedSetPeerNotTalking();
    }

    return () => {
      debouncedSetPeerNotTalking.cancel?.();
    };
  }, [audioVolume, isMicMuted, debouncedSetPeerNotTalking]);

  const consumerVideoStream = useMemo(() => {
    if (!hasValidCameraStream || !stream) return null;
    return getMediaStreamSource(stream, 'videoType');
  }, [hasValidCameraStream, stream]);

  const handleVideoReady = () => {
    if (readyTimeoutRef.current) {
      window.clearTimeout(readyTimeoutRef.current);
    }

    // Add extra delay to ensure no black frame flash
    readyTimeoutRef.current = window.setTimeout(() => {
      setIsVideoReady(true);
    }, PIP_COMPACT_VIDEO_READY_DELAY_MS);
  };

  useEffect(() => {
    // Reset readiness whenever the bound stream changes.
    setIsVideoReady(false);
    if (readyTimeoutRef.current) {
      window.clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }

    const el = videoRef.current;
    if (!el) return;

    el.srcObject = consumerVideoStream;
    el.play?.().catch(() => {});

    return () => {
      el.srcObject = null;
      if (readyTimeoutRef.current) {
        window.clearTimeout(readyTimeoutRef.current);
        readyTimeoutRef.current = null;
      }
    };
  }, [consumerVideoStream]);

  // Ensure remote consumer resumes while shown in compact PiP.
  useEffect(() => {
    if (type !== 'videoType') return;

    // IMPORTANT:
    // Do NOT pause on unmount here.
    // During PiP mode transitions we render both normal and compact layers briefly;
    // if compact unmount pauses the consumer, the normal VideoBox layer may not re-run
    // its "resume" effect, leaving the consumer paused and the video stuck loading/black.
    // VideoBox will handle pausing when it's truly no longer needed.
    if (hasValidCameraStream) {
      handleVisibilityChange(true);
    }
  }, [handleVisibilityChange, hasValidCameraStream, type]);

  const avatarSizePx = Math.round(PIP_COMPACT_AVATAR_SIZE_PX * scale);
  const micBadgeSizePx = Math.max(0, Math.round(PIP_COMPACT_MIC_BADGE_SIZE_PX * scale));
  const micBadgeBorderPx = Math.max(0, Math.round(PIP_COMPACT_MIC_BADGE_BORDER_PX * scale));
  const micIconSizePx = Math.max(0, Math.round(PIP_COMPACT_MIC_ICON_SIZE_PX * scale));
  const activeRingPaddingPx = Math.max(0, Math.round(PIP_COMPACT_ACTIVE_RING_PADDING_PX * scale));
  const activeRingBorderPx = Math.max(0, Math.round(PIP_COMPACT_ACTIVE_RING_BORDER_PX * scale));
  const activeRingShadowPx = Math.max(0, Math.round(PIP_COMPACT_ACTIVE_RING_SHADOW_PX * scale));

  const isActiveRing = isPeerTalking && activeRingBorderPx > 0 && activeRingPaddingPx > 0;
  const tileStyle = {
    '--pip-avatar-size': `${avatarSizePx}px`,
    '--pip-mic-badge-size': `${micBadgeSizePx}px`,
    '--pip-mic-badge-border': `${micBadgeBorderPx}px`,
    '--pip-active-ring-padding': `${activeRingPaddingPx}px`,
    '--pip-active-ring-border': `${activeRingBorderPx}px`,
    '--pip-active-ring-shadow': `${activeRingShadowPx}px`,
  } as React.CSSProperties;

  // render
  return (
    <div className="pip-compact-avatar-tile" style={tileStyle}>
      <div
        className={`pip-compact-avatar-active-ring ${isActiveRing ? 'is-active' : ''}`}
        aria-label={isActiveRing ? 'Active speaker' : undefined}
      >
        <div className="pip-compact-avatar-circle">
          {hasValidCameraStream ? (
            <>
              <video
                id={`${id}-pip-compact`}
                ref={videoRef}
                className="pip-compact-video"
                autoPlay
                playsInline
                muted
                poster={poster}
                onLoadedData={handleVideoReady}
                onCanPlay={handleVideoReady}
                onPlaying={handleVideoReady}
              />
              {poster && !isVideoReady && <img className="pip-compact-video-loader" src={poster} alt="" />}
            </>
          ) : (
            <AvatarCircle peerId={peerId} avatarImgSize={avatarSizePx} />
          )}
        </div>
      </div>

      {isMicMuted && micBadgeSizePx > 0 && micIconSizePx > 0 && (
        <div className="pip-compact-mic-badge" aria-label="Microphone muted">
          <BsMicMuteFill size={micIconSizePx} color="#ffffff" />
        </div>
      )}
    </div>
  );
});

export default PipCompactAvatar;
